import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchCallAllDataByIDAPI } from "../../utils/api";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import withAuth from "../../utils/withAuth";
import ReportNavBar from "../../components/ReportNavBar";

const SKELETON_COUNT = 10;

const SkeletonTableCard = () => (
    <div className="col-md-4 col-lg-3 col-sm-6 mb-4">
        <div className="card h-100 shadow-sm">
            <div className="card-body p-2">
                <div className="skeleton" style={{ height: 18, width: "70%", marginBottom: 10 }} />
                <div className="skeleton" style={{ height: 14, width: "100%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 14, width: "100%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 14, width: "80%", marginBottom: 6 }} />
            </div>
        </div>
    </div>
);

const TodayLiveCall = () => {
    const [process, setProcess] = useState([]);     // API "rows"
    const [summary, setSummary] = useState(null);   // API "summary"
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();
    const ppcid = localStorage.getItem("ppcdata");

    useEffect(() => {
        if (!ppcid) return navigate("/");
        navigate("/today-live-call-reports");
        getLiveCallWithProcessName(ppcid);
    }, [ppcid]);

    const getLiveCallWithProcessName = async (id) => {
        setIsLoading(true);
        try {
            const res = await fetchCallAllDataByIDAPI(`ppc-team-get-live-call-with-proccess-name-by-id/${id}`);

            if (res?.data?.status === 1) {
                // ✅ your backend now returns: process: { rows:[], summary:{} }
                const p = res?.data?.process || {};
                setProcess(Array.isArray(p?.rows) ? p.rows : []);
                setSummary(p?.summary || null);
            } else {
                toast.error(res?.data?.message || "Failed to load process");
                setProcess([]);
                setSummary(null);
            }
        } catch (e) {
            console.error(e);
            toast.error("Network/CORS error while loading process");
            setProcess([]);
            setSummary(null);
        } finally {
            setIsLoading(false);
        }
    };

    // Suffix → Person/Group name map (extend freely)
    const PERSON_BY_CODE = {
        AM: "Amit",
        KA: "Kanika",
        DI: "Dinesh",

        // prefer 2-token matches first
        "RI_MA": "Mahesh",
        "RCP_MN": "Rahul Moti Nagar",

        // 1-token fallbacks
        MA: "Mahadesh",
        RNOI: "RAVI NOIDA",
        G10: "RAVI",
        NOI: "ABHISHEK NOIDA",
        BA: "Banglore",
        G19: "Kanta",
        RCP: "RCP",
        MY: "Mysore",
        JGT: "Jagat",
        KOL: "Kolkata",
        TN: "MAHALAXMI",
        SL: "SHAILENDRA",
        RI: "RICHA",
        SCP: "SHYAM",
        MU: "MURGESH",
        PU: "PUSHPENDAR",
        SP: "ASTRO",
        UN: "UTTAM NAGAR",
    };

    // Helper: choose best match → try last2 tokens, then last1
    const deriveGroupName = (skillRaw) => {
        const skill = String(skillRaw || "").trim();
        if (!skill) return "Other";
        const parts = skill.split("_").filter(Boolean);
        const n = parts.length;
        const last = parts[n - 1]?.toUpperCase() || "";
        const last2 = n >= 2 ? `${parts[n - 2].toUpperCase()}_${last}` : null;

        if (last2 && PERSON_BY_CODE[last2]) return PERSON_BY_CODE[last2];
        if (PERSON_BY_CODE[last]) return PERSON_BY_CODE[last];

        return last2 ? (PERSON_BY_CODE[last2] || last2) : (last || "Other");
    };

    // Build grouped cards incl. Spend
    const groupedData = useMemo(() => {
        const groups = {};
        (process || []).forEach((row) => {
            const code = String(row?.skillname || "");
            const calls = Number(row?.unique_calls || 0);
            const orders = Number(row?.order_count || 0);
            const spend = Number(row?.spend_today || 0); // ✅ from API
            const groupName = deriveGroupName(code);

            if (!groups[groupName]) {
                groups[groupName] = { name: groupName, totalCalls: 0, totalOrders: 0, totalSpend: 0, items: [] };
            }

            groups[groupName].totalCalls += calls;
            groups[groupName].totalOrders += orders;
            groups[groupName].totalSpend += spend;

            groups[groupName].items.push({ code, calls, orders, spend });
        });

        Object.values(groups).forEach((g) => {
            g.perf = g.totalCalls ? (g.totalOrders / g.totalCalls) * 100 : 0;
            g.items.sort((a, b) =>
                b.calls !== a.calls ? b.calls - a.calls : a.code.localeCompare(b.code)
            );
        });

        return Object.values(groups).sort((a, b) =>
            b.totalCalls !== a.totalCalls ? b.totalCalls - a.totalCalls : a.name.localeCompare(b.name)
        );
    }, [process]);

    // ✅ Prefer backend summary (accurate + includes spend)
    const liveCallCount = useMemo(() => {
        if (summary && typeof summary.total_unique_calls !== "undefined") return Number(summary.total_unique_calls) || 0;
        return (process || []).reduce((s, r) => s + (Number(r?.unique_calls) || 0), 0);
    }, [process, summary]);

    const liveOrderCount = useMemo(() => {
        if (summary && typeof summary.total_orders_created !== "undefined") return Number(summary.total_orders_created) || 0;
        // fallback: older key name (if any)
        if (summary && typeof summary.total_orders_created !== "undefined") return Number(summary.total_orders_created) || 0;
        return (process || []).reduce((s, r) => s + (Number(r?.order_count) || 0), 0);
    }, [process, summary]);

    const liveSpendToday = useMemo(() => {
        if (summary && typeof summary.total_spend_today !== "undefined") return Number(summary.total_spend_today) || 0;
        return (process || []).reduce((s, r) => s + (Number(r?.spend_today) || 0), 0);
    }, [process, summary]);

    const livePerf = useMemo(
        () => (liveCallCount ? (liveOrderCount / liveCallCount) * 100 : 0),
        [liveCallCount, liveOrderCount]
    );

    const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
    const fmtMoney = (n) =>
        Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
        <div className="misDashboard">
            <Helmet>
                <title>SATKARTAR::DASHBOARD</title>
            </Helmet>

            <style>{`
        .skeleton{position:relative;overflow:hidden;background:#eee;border-radius:6px}
        .skeleton:after{content:"";position:absolute;inset:0;transform:translateX(-100%);
          background:linear-gradient(90deg,transparent,rgba(255,255,255,.6),transparent);
          animation:shimmer 1.2s infinite}
        @keyframes shimmer{100%{transform:translateX(100%)}}
        .mini-table th,.mini-table td{font-size:12px; padding:6px 8px;}
      `}</style>

            <ContentWrapper>
                <div className="row">
                    <div className="col-12">
                        <ReportNavBar />
                    </div>
                </div>

                <div className="">
                    <h5 className="livecallcount">
                        Live Calls: {isLoading ? "..." : fmt(liveCallCount)}
                        {"  |  "}
                        Orders: {isLoading ? "..." : fmt(liveOrderCount)}
                        {"  |  "}
                        Spend: {isLoading ? "..." : `₹${fmtMoney(liveSpendToday)}`}
                        {"  |  "}
                        Perf: {isLoading ? "..." : `${livePerf.toFixed(1)}%`}
                    </h5>
                </div>

                <div className="row mt-3 livecallbody">
                    {isLoading
                        ? Array.from({ length: SKELETON_COUNT }).map((_, i) => <SkeletonTableCard key={`sk-${i}`} />)
                        : groupedData.map((group, idx) => (
                            <div className="col-md-6 col-lg-4 mb-3" key={idx}>
                                <div className="card shadow-sm">
                                    <div className="card-body p-2">
                                        {/* ✅ make card body a scroll container */}
                                        <div className="p-0" style={{ maxHeight: 260, overflowY: "auto" }}>

                                            <table className="table table-sm table-bordered mb-0 mini-table">
                                                <thead className="stickyHead">
                                                    {/* ✅ Sticky Header like your screenshot */}
                                                    <tr>
                                                        <th colSpan={5} className="skillStickyHeader">
                                                            <div className="d-flex justify-content-between">
                                                                <div className="sh-left">{group.name}</div>
                                                                <div className="sh-right">
                                                                    <span>Calls: <b>{fmt(group.totalCalls)}</b></span>
                                                                    <span>| Orders: <b>{fmt(group.totalOrders)}</b></span>
                                                                    <span>| ₹{fmtMoney(group.totalSpend)}</span>
                                                                </div>
                                                            </div>
                                                        </th>
                                                    </tr>

                                                    {/* ✅ Column titles */}
                                                    <tr className="colHead">
                                                        <th>Skill</th>
                                                        <th style={{ width: 30, textAlign: "right" }}>Calls</th>
                                                        <th style={{ width: 30, textAlign: "right" }}>Orders</th>
                                                        <th style={{ width: 60, textAlign: "right" }}>Spend</th>
                                                        <th style={{ width: 45, textAlign: "right" }}>Perf</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {group.items.map((item, ix) => (
                                                        <tr key={ix}>
                                                            <td>{item.code}</td>
                                                            <td style={{ textAlign: "right" }}>{fmt(item.calls)}</td>
                                                            <td style={{ textAlign: "right" }}>{fmt(item.orders)}</td>
                                                            <td style={{ textAlign: "right" }}>₹{fmtMoney(item.spend)}</td>
                                                            <td
                                                                style={{
                                                                    textAlign: "right",
                                                                    fontWeight: 800,
                                                                    display: "flex",
                                                                    justifyContent: "flex-end",
                                                                    alignItems: "center",
                                                                    gap: 4,
                                                                    color:
                                                                        item.calls === 0
                                                                            ? "#999"
                                                                            : (item.orders / item.calls) >= 0.15
                                                                                ? "#2e7d32"
                                                                                : "#d32f2f",
                                                                }}
                                                            >
                                                                {item.calls === 0 ? (
                                                                    <>
                                                                        <span style={{ fontSize: 12 }}>—</span>
                                                                        <span>0.0%</span>
                                                                    </>
                                                                ) : (item.orders / item.calls) >= 0.15 ? (
                                                                    <>
                                                                        <span style={{ fontSize: 12 }}>↑</span>
                                                                        <span>{((item.orders / item.calls) * 100).toFixed(1)}%</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <span style={{ fontSize: 12 }}>↓</span>
                                                                        <span>{((item.orders / item.calls) * 100).toFixed(1)}%</span>
                                                                    </>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                    {!isLoading && groupedData.length === 0 && (
                        <div className="col-12">
                            <div className="alert alert-info mb-0">No data for today.</div>
                        </div>
                    )}
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(TodayLiveCall);
